import React, { useState, useEffect } from 'react';
import { HiCheck } from 'react-icons/hi';
import { historiqueService } from '../../services/historiqueService';

function HistoriqueManagement({ selectedEdition }) {
  const [historiqueData, setHistoriqueData] = useState({
    theme: '',
    participants: '',
    resume: '',
    highlights: []
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadHistoriqueData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(historiqueData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [historiqueData, originalData]);

  const loadHistoriqueData = async () => {
    if (!selectedEdition) return;

    try {
      setLoading(true);
      console.log('Loading historique data for edition:', selectedEdition);

      // Try to get the full historique data
      const data = await historiqueService.getHistoriqueData(selectedEdition);

      // Extract editable fields
      const editableData = {
        theme: data.theme,
        participants: data.participants,
        resume: data.resume,
        highlights: data.highlights
      };

      // Check if this is default/empty data
      if (!data.theme || data.theme === 'Thème non défini' ||
        !data.resume || data.resume === 'Résumé non disponible' ||
        data.highlights.length === 0) {
        setIsDatabaseEmpty(true);
        // Set default editable data
        const defaultData = {
          theme: '',
          participants: '',
          resume: '',
          highlights: []
        };
        setHistoriqueData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        setHistoriqueData(editableData);
        setOriginalData(JSON.parse(JSON.stringify(editableData)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading historique data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        theme: '',
        participants: '',
        resume: '',
        highlights: []
      };
      setHistoriqueData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await historiqueService.updateHistoriqueData(historiqueData, selectedEdition);
      setOriginalData(JSON.parse(JSON.stringify(historiqueData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
    } catch (error) {
      console.error('Error saving historique data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setHistoriqueData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleUpdateTheme = (value) => {
    setHistoriqueData(prev => ({
      ...prev,
      theme: value
    }));
  };

  const handleUpdateParticipants = (value) => {
    setHistoriqueData(prev => ({
      ...prev,
      participants: value
    }));
  };

  const handleUpdateResume = (value) => {
    setHistoriqueData(prev => ({
      ...prev,
      resume: value
    }));
  };

  const handleUpdateHighlights = (value) => {
    const highlightsArray = value.split('\n').filter(highlight => highlight.trim() !== '');
    setHistoriqueData(prev => ({
      ...prev,
      highlights: highlightsArray
    }));
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>Les données historiques sont vides pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour les initialiser.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Historique Data Form */}
      <div className="section-header">
        <h3>Données Historiques de l'Édition {selectedEdition}</h3>
      </div>

      <div className="form-group">
        <label>Thème principal:</label>
        <input
          type="text"
          value={historiqueData.theme}
          onChange={(e) => handleUpdateTheme(e.target.value)}
          placeholder="Ex: Innovation et Recherche Médicale"
        />
      </div>

      <div className="form-group">
        <label>Nombre de participants:</label>
        <input
          type="text"
          value={historiqueData.participants}
          onChange={(e) => handleUpdateParticipants(e.target.value)}
          placeholder="Ex: 250 participants"
        />
      </div>

      <div className="form-group">
        <label>Résumé de l'édition:</label>
        <textarea
          value={historiqueData.resume}
          onChange={(e) => handleUpdateResume(e.target.value)}
          rows="6"
          placeholder="Décrivez le résumé général de cette édition..."
        />
      </div>

      <div className="form-group">
        <label>Points forts (un par ligne):</label>
        <textarea
          value={historiqueData.highlights.join('\n')}
          onChange={(e) => handleUpdateHighlights(e.target.value)}
          rows="5"
          placeholder="Entrez chaque point fort sur une nouvelle ligne..."
        />
      </div>

      {/* Info Section */}
      <div className="section-header">
        <h3>Informations Automatiques</h3>
        <p style={{ fontSize: '14px', color: '#ffffffff', margin: 0 }}>
          Les données suivantes sont automatiquement générées à partir des autres sections
        </p>
      </div>

      <div style={{ background: '#ffffff', padding: '20px', borderRadius: '4px', border: '1px solid #e1e5e9' }}>
        <p><strong>Données automatiques incluses dans l'historique:</strong></p>
        <ul style={{ marginBottom: 0, color: '#666' }}>
          <li>Liste des intervenants (présentiel et en ligne)</li>
          <li>Liste des axes du congrès</li>
          <li>Membres du comité d'organisation</li>
          <li>Statistiques (nombre de conférences, participants, etc.)</li>
          <li>Dates et lieu (depuis les données de base de l'édition)</li>
        </ul>
      </div>
    </div>
  );
}

export default HistoriqueManagement;
